import { useState, useEffect } from 'react';
import AgentSidebar from './AgentSidebar';
import AgentHeader from './AgentHeader';
import AgentPersonalityForm from './AgentPersonalityForm';
import AgentKnowledgeBase from './AgentKnowledgeBase';
import AgentIntegrations from './AgentIntegrations';
import CreateAgentModal from './CreateAgentModal';

/**
 * Container da página de configuração de IA.
 * Recebe estado e ações do hook useAiAgents (usado no App) e teams para exibir roles.
 * Layout: sidebar (Meus Agentes) + área principal (configuração do agente selecionado).
 * Ponto de integração: substituir callbacks por chamadas à API quando backend estiver disponível.
 *
 * @param {Object} props
 * @param {Array} props.agents
 * @param {string | null} props.selectedAgentId
 * @param {(id: string | null) => void} props.setSelectedAgentId
 * @param {Object | null} props.selectedAgent
 * @param {(payload: Partial<Object>) => Object} props.createAgent
 * @param {(id: string, updates: Partial<Object>) => void} props.updateAgent
 * @param {(id: string) => void} [props.deleteAgent]
 * @param {(agentId: string, source: Object) => void} props.addKnowledgeSource
 * @param {(agentId: string, sourceId: string) => void} props.removeKnowledgeSource
 * @param {(agentId: string, name: string) => void} props.addIntegration
 * @param {(agentId: string, integrationId: string, status: string) => void} [props.setIntegrationStatus]
 * @param {(agentId: string, integrationId: string) => void} [props.removeIntegration]
 * @param {Record<string, { name: string }>} [props.teamsMap]
 */
export default function AiConfigPanel({
  agents,
  selectedAgentId,
  setSelectedAgentId,
  selectedAgent,
  createAgent,
  updateAgent,
  deleteAgent,
  addKnowledgeSource,
  removeKnowledgeSource,
  addIntegration,
  setIntegrationStatus,
  removeIntegration,
  teamsMap = {},
}) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(false);

  const teamsArray = Object.values(teamsMap);

  const handleSaveHeader = () => {
    // Dados já estão atualizados via updateAgent nos campos; aqui apenas feedback visual (mock).
    setSaveFeedback(true);
  };

  useEffect(() => {
    if (!saveFeedback) return;
    const t = setTimeout(() => setSaveFeedback(false), 2000);
    return () => clearTimeout(t);
  }, [saveFeedback]);

  const handleCreateAgent = (payload) => {
    createAgent(payload);
    setShowCreateModal(false);
  };

  const handleAddKnowledgeSource = (source) => {
    if (!selectedAgent) return;
    addKnowledgeSource(selectedAgent.id, source);
  };

  const handleAddIntegration = (name) => {
    if (!selectedAgent) return;
    addIntegration(selectedAgent.id, name);
  };

  // Empty state: sem agentes
  if (agents.length === 0) {
    return (
      <div className="flex-1 bg-appPage flex overflow-hidden">
        <AgentSidebar
          agents={[]}
          selectedAgentId={null}
          onSelectAgent={setSelectedAgentId}
          onCreateAgent={() => setShowCreateModal(true)}
          teamsMap={teamsMap}
        />
        <div className="flex-1 flex items-center justify-center bg-appPage">
          <div className="text-center">
            <p className="text-appTextMuted mb-4">Nenhum agente configurado.</p>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2.5 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90"
            >
              + Criar Agente
            </button>
          </div>
        </div>
        <CreateAgentModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateAgent}
          teams={teamsArray}
        />
      </div>
    );
  }

  // Sem agente selecionado (edge case)
  if (!selectedAgent) {
    return (
      <div className="flex-1 bg-appPage flex overflow-hidden">
        <AgentSidebar
          agents={agents}
          selectedAgentId={selectedAgentId}
          onSelectAgent={setSelectedAgentId}
          onCreateAgent={() => setShowCreateModal(true)}
          teamsMap={teamsMap}
        />
        <div className="flex-1 flex items-center justify-center bg-appPage">
          <p className="text-appTextMuted">Selecione um agente na lista.</p>
        </div>
        <CreateAgentModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateAgent}
          teams={teamsArray}
        />
      </div>
    );
  }

  return (
    <div className="flex-1 bg-appPage flex overflow-hidden">
      <AgentSidebar
        agents={agents}
        selectedAgentId={selectedAgentId}
        onSelectAgent={setSelectedAgentId}
        onCreateAgent={() => setShowCreateModal(true)}
        teamsMap={teamsMap}
      />

      <div className="flex-1 overflow-y-auto p-12">
        <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className={saveFeedback ? 'ring-2 ring-emerald-400 ring-offset-2 rounded-3xl' : ''}>
            <AgentHeader
              agent={selectedAgent}
              teamsMap={teamsMap}
              onSave={handleSaveHeader}
            />
            {saveFeedback && (
              <p className="text-emerald-600 text-sm font-medium text-center mt-2" role="status">
                Alterações salvas.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <AgentPersonalityForm
                agent={selectedAgent}
                onUpdate={(updates) => updateAgent(selectedAgent.id, updates)}
              />
            </div>

            <div className="space-y-6">
              <AgentKnowledgeBase
                sources={selectedAgent.knowledgeBase || []}
                onAddSource={handleAddKnowledgeSource}
                onRemoveSource={(sourceId) =>
                  removeKnowledgeSource(selectedAgent.id, sourceId)
                }
              />
              <AgentIntegrations
                integrations={selectedAgent.integrations || []}
                onAddIntegration={handleAddIntegration}
                onToggleStatus={
                  setIntegrationStatus
                    ? (integrationId, status) =>
                        setIntegrationStatus(selectedAgent.id, integrationId, status)
                    : undefined
                }
                onRemove={
                  removeIntegration
                    ? (integrationId) =>
                        removeIntegration(selectedAgent.id, integrationId)
                    : undefined
                }
              />
            </div>
          </div>
        </div>
      </div>

      <CreateAgentModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateAgent}
        teams={teamsArray}
      />
    </div>
  );
}
